/**
 * CSS Purging Utility - Remove unused CSS to improve performance
 * This helps eliminate the "Reduce unused CSS" performance issue
 */

export const purgeUnusedCSS = () => {
  console.log('🔍 Starting CSS purge analysis...');
  
  // Get all CSS rules
  const styleSheets = Array.from(document.styleSheets);
  const allCSSRules = [];
  const usedSelectors = new Set();
  
  // Collect all CSS rules
  styleSheets.forEach(sheet => {
    try {
      const rules = Array.from(sheet.cssRules || sheet.rules || []);
      rules.forEach(rule => {
        if (rule.selectorText) {
          allCSSRules.push(rule.selectorText);
        }
      });
    } catch (e) {
      // Cross-origin stylesheets will throw errors
      console.log('⚠️ Could not access stylesheet:', e);
    }
  });
  
  // Collect all used classes and IDs from DOM
  const allElements = document.querySelectorAll('*');
  allElements.forEach(element => {
    if (element.className) {
      element.className.split(' ').forEach(className => {
        if (className.trim()) {
          usedSelectors.add(`.${className.trim()}`);
        }
      });
    }
    if (element.id) {
      usedSelectors.add(`#${element.id}`);
    }
  });
  
  // Analyze unused selectors
  const unusedSelectors = [];
  allCSSRules.forEach(selector => {
    if (selector.includes('.') || selector.includes('#')) {
      // Check if selector is used
      let isUsed = false;
      
      // Check if selector matches any used selectors
      usedSelectors.forEach(usedSelector => {
        if (selector.includes(usedSelector.replace('.', '').replace('#', ''))) {
          isUsed = true;
        }
      });
      
      // Check if selector is a common element selector
      const commonElements = ['body', 'html', 'div', 'span', 'p', 'a', 'button', 'input', 'img'];
      if (commonElements.some(el => selector.includes(el))) {
        isUsed = true;
      }
      
      if (!isUsed) {
        unusedSelectors.push(selector);
      }
    }
  });
  
  console.log('📊 CSS Purge Results:');
  console.log(`Total CSS rules: ${allCSSRules.length}`);
  console.log(`Used selectors: ${usedSelectors.size}`);
  console.log(`Unused selectors: ${unusedSelectors.length}`);
  console.log(`Potential savings: ${(unusedSelectors.length * 0.1).toFixed(2)} KiB`);
  
  return {
    totalRules: allCSSRules.length,
    usedSelectors: Array.from(usedSelectors),
    unusedSelectors,
    estimatedSavings: (unusedSelectors.length * 0.1).toFixed(2)
  };
};

// YouTube CSS optimization
export const optimizeYouTubeEmbeds = () => {
  console.log('🎥 Optimizing YouTube embeds...');
  
  const youtubeEmbeds = document.querySelectorAll('iframe[src*="youtube.com"]');
  youtubeEmbeds.forEach(embed => {
    // Add loading optimization
    embed.setAttribute('loading', 'lazy');
    
    // Add CSS containment to prevent unused CSS loading
    embed.style.contain = 'layout style paint';
    embed.style.isolation = 'isolate';
    
    // Add performance class
    embed.classList.add('youtube-embed');
    
    console.log('✅ YouTube embed optimized:', embed.src);
  });
  
  return youtubeEmbeds.length;
};

// Critical CSS extraction
export const extractCriticalCSS = () => {
  console.log('⚡ Extracting critical CSS...');
  
  const aboveFoldElements = document.querySelectorAll('body > *, .hero, .header, .nav');
  const criticalCSS = new Set();
  
  aboveFoldElements.forEach(element => {
    // Get element classes without unused styles variable
    const elementClasses = element.className.split(' ').filter(c => c.trim());
    
    elementClasses.forEach(className => {
      if (className.trim()) {
        criticalCSS.add(`.${className.trim()}`);
      }
    });
  });
  
  console.log(`Critical CSS classes: ${criticalCSS.size}`);
  return Array.from(criticalCSS);
};

// Performance monitoring
export const measureCSSPerformance = () => {
  const start = performance.now();
  
  // Force a reflow to measure CSS processing time
  void document.body.offsetHeight;
  
  const end = performance.now();
  const cssTime = end - start;
  
  console.log(`⚡ CSS processing time: ${cssTime.toFixed(2)}ms`);
  
  // Performance recommendations
  if (cssTime > 16) {
    console.log('⚠️ CSS processing is slow. Consider:');
    console.log('   - Removing unused CSS');
    console.log('   - Using CSS containment');
    console.log('   - Lazy loading below-the-fold styles');
  }
  
  return cssTime;
};
